import { useState, useEffect } from "react"
import { Lightbulb, Target, Compass } from 'lucide-react'
import { useLanguage } from "./LanguageContext"
import { loadAppConfiguration } from "./ContentLoader"
import Navigation, { Footer } from "./Navigation"
import "./components/AboutPage.css"

const AboutPage = ({ onReturnHome }) => {
  // Get current language from context
  const { language } = useLanguage()

  // State for app configuration and about content
  const [appConfig, setAppConfig] = useState(null)
  const [aboutContent, setAboutContent] = useState(null)
  const [isLoading, setIsLoading] = useState(true)

  // Helper function to get localized text from content
  const t = (path, defaultText = "") => {
    // If appConfig isn't loaded yet, return the default text
    if (!appConfig) return defaultText

    // Split the path by dots to navigate nested objects
    const keys = path.split(".")
    let value = appConfig

    // Navigate through the object
    for (const key of keys) {
      if (!value || !value[key]) return defaultText
      value = value[key]
    }

    // Return the localized text if it's an object with language keys
    if (value && typeof value === "object" && value[language.code]) {
      return value[language.code]
    }

    // Return the value if it's a string or the default text
    return typeof value === "string" ? value : value.en || defaultText
  }

  // Load app configuration and about content
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true)
      try {
        // Load the app configuration
        const companyId = new URLSearchParams(window.location.search).get("company") || null
        const config = await loadAppConfiguration(language.code, companyId, false)
        setAppConfig(config)

        // Apply accent color if provided
        if (config?.branding?.accentColor) {
          document.documentElement.style.setProperty("--accent-color", config.branding.accentColor)
        }

        // Load the about content
        const aboutResponse = await fetch(`/content/main/app-about.json`)
        if (!aboutResponse.ok) {
          throw new Error(`Failed to load about content: ${aboutResponse.status}`)
        }
        const aboutData = await aboutResponse.json()
        setAboutContent(aboutData)
      } catch (error) {
        console.error("Failed to load data:", error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [language.code])

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    )
  }

  // Map section icons based on index
  const getSectionIcon = (index) => {
    switch (index) {
      case 0:
        return <Lightbulb />
      case 1:
        return <Target />
      case 2:
        return <Compass />
      default:
        return <Lightbulb />
    }
  }

  // Handle navigation
  const handleNavigation = (page) => {
    if (page === "home") {
      onReturnHome()
    }
  }

  return (
    <div className="game-selector-container">
      <Navigation 
        appConfig={appConfig} 
        currentPage="about" 
        onNavigate={handleNavigation} 
        t={t} 
      />

      {/* About Content */}
      <main className="main-content">
        {/* Hero Section */}
        <section className="hero-section">
          <div className="hero-content">
            <div className="hero-content-inner">
              <h1 className="hero-title">{aboutContent?.title?.[language.code] || "About Blinkli"}</h1>
              <p className="hero-subtitle">
                {aboutContent?.subtitle?.[language.code] || "Interactive learning, one step at a time"}
              </p>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="about-main">
          {/* Main Sections */}
          <div className="about-sections">
            {aboutContent?.sections?.map((section, index) => (
              <section key={index} className="about-section">
                <div className="section-icon">{getSectionIcon(index)}</div>
                <h2 className="section-heading">{section.heading?.[language.code] || `Section ${index + 1}`}</h2>
                <p className="section-content">{section.content?.[language.code] || ""}</p>
              </section>
            ))}
          </div>

          {aboutContent?.aboutMe && (
            <section className="about-me-section">
              <div className="about-me-container">
                <div className="about-me-content">
                  <div className="about-me-image-container">
                    {aboutContent.aboutMe.image && (
                      <img
                        src={aboutContent.aboutMe.image || "/placeholder.svg"}
                        alt={aboutContent.aboutMe.title?.[language.code] || "Laura Rademaker"}
                        className="about-me-image"
                      />
                    )}
                  </div>
                  <div className="about-me-text">
                    <h2 className="about-me-heading">{aboutContent.aboutMe.heading?.[language.code] || "About Me"}</h2>

                    <h3 className="about-me-title">
                      {aboutContent.aboutMe.title?.[language.code] || "Laura Rademaker"}
                    </h3>
                    <h4 className="about-me-subtitle">
                      {aboutContent.aboutMe.subtitle?.[language.code] || "UX Coach"}
                    </h4>
                    <p className="about-me-description">{aboutContent.aboutMe.content?.[language.code] || ""}</p>
                    {aboutContent.aboutMe.linkUrl && (
                      <a
                        href={aboutContent.aboutMe.linkUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="about-me-link"
                      >
                        {aboutContent.aboutMe.linkText?.[language.code] || "Learn more"}
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* Contact Section */}
          {aboutContent?.contact && (
            <section className="contact-section">
              <h2 className="contact-heading">{aboutContent.contact.heading?.[language.code] || "Get in Touch"}</h2>
              <p className="contact-email">
                Email: <a href={`mailto:${aboutContent.contact.email}`}>{aboutContent.contact.email}</a>
              </p>
              <p className="contact-website">
                Website:{" "}
                <a href={aboutContent.contact.website} target="_blank" rel="noopener noreferrer">
                  {aboutContent.contact.website}
                </a>
              </p>
            </section>
          )}
        </section>
      </main>

      <Footer appConfig={appConfig} t={t} />
    </div>
  )
}

export default AboutPage
